import { Card as BlueprintCard, Collapse } from "@blueprintjs/core";
import React, { useState } from "react";
import styled from "styled-components";
import Link from "./Link";

interface CardProps {
  className?: string;
  header?: React.ReactNode;
  headerAside?: React.ReactNode;
  headerLinkTo?: string;
  linkTo?: string;
  isOpenDefault?: boolean;
  interactive?: boolean;
}

const StyledBlueprintCard = styled(BlueprintCard)`
  font-size: var(--global-font-size-s);
  line-height: 125%;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
  padding: 0;

  ${Link}:hover & {
    background: #fafafa;
  }
`;

interface HeaderProps {
  isOpen: boolean;
  interactive?: boolean;
}

const CardBody = styled(Collapse)`
  padding: ${(props) => (props.isOpen ? "20px" : "0")};
`;

const CardHeader = styled.header`
  padding: 20px;

  transition: background 0.2s;
  overflow: hidden;
  border-radius: 10px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  ${(props: HeaderProps) =>
    props.isOpen &&
    `
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      border-bottom: 1px solid #eaeaea;
    `}

  ${(props: HeaderProps) =>
    props.interactive &&
    `
      cursor: pointer;
      &:hover {
        background: #fafafa;
      }
    `}

  ${Link}:hover & {
    background: var(--global-purple-highlight);
    color: #fff;
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      color: #fff;
    }
  }
`;

const Card: React.FC<React.PropsWithChildren<CardProps>> = ({
  children,
  className,
  linkTo,
  header,
  headerAside,
  headerLinkTo,
  isOpenDefault = false,
  interactive = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(!interactive || isOpenDefault);
  const onHeaderClick = () => {
    setIsOpen((isOpen) => !isOpen);
  };
  let cardHeader =
    header === undefined ? undefined : (
      <CardHeader
        isOpen={isOpen}
        interactive={interactive}
        onClick={interactive ? onHeaderClick : undefined}
      >
        <div>{header}</div>
        {headerAside ? <aside>{headerAside}</aside> : undefined}
      </CardHeader>
    );
  if (headerLinkTo !== undefined) {
    cardHeader = <Link to={headerLinkTo}>{cardHeader}</Link>;
  }

  let card = (
    <StyledBlueprintCard className={className}>
      {cardHeader}
      <CardBody isOpen={!header || isOpen}>{children}</CardBody>
    </StyledBlueprintCard>
  );
  if (linkTo !== undefined) {
    card = <Link to={linkTo}>{card}</Link>;
  }
  return card;
};
export default Card;
